import React, { useState } from 'react'
import { Button, ListGroup, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

export interface DocumentWorkflowStartChoiceModalProps {
  show: boolean
  onHide: () => void
  availableDefinitions: Array<{ id: number; name: string }>
  onStart: (definitionId: number) => Promise<void>
  /** No document type or no definitions for this type */
  emptyMessage?: string
}

export const DocumentWorkflowStartChoiceModal: React.FC<DocumentWorkflowStartChoiceModalProps> = ({
  show,
  onHide,
  availableDefinitions,
  onStart,
  emptyMessage,
}) => {
  const { t } = useTranslation()
  const [startingId, setStartingId] = useState<number | null>(null)

  const handleStart = async (id: number) => {
    setStartingId(id)
    try {
      await onStart(id)
      onHide()
    } finally {
      setStartingId(null)
    }
  }

  const isEmpty = !availableDefinitions?.length

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('document_workflow.start_workflow')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isEmpty ? (
          <p className="text-muted mb-0">
            {emptyMessage ?? t('document_workflow.no_definitions_to_start')}
          </p>
        ) : (
          <ListGroup variant="flush">
            {availableDefinitions.map((def) => (
              <ListGroup.Item
                key={def.id}
                as="button"
                type="button"
                action
                className="d-flex align-items-center justify-content-between"
                onClick={() => handleStart(def.id)}
                disabled={startingId != null}
              >
                <span>{def.name}</span>
                {startingId === def.id ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="bi bi-play-fill text-primary" />
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      {isEmpty && (
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('close')}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  )
}
